import { escape } from "lodash-es";

/**
 * Inject <mark> tags into a single text item's string for PDF search highlighting.
 *
 * @param str - The text item's content
 * @param itemStart - Start offset of this item within the concatenated page text
 * @param itemEnd - End offset of this item within the concatenated page text
 * @param pageIndex - Page index (0-based)
 * @param pageMatches - Array of match start offsets (global within concatenated page text)
 * @param pageMatchesLength - Array of match lengths (parallel to pageMatches)
 * @param selected - Currently selected match (pageIdx + matchIdx)
 * @param query - The search query, used to snap highlight boundaries for fully-contained matches
 */
export function highlightTextItem(
  str: string,
  itemStart: number,
  itemEnd: number,
  pageIndex: number,
  pageMatches: number[],
  pageMatchesLength: number[] | undefined,
  selected: { pageIdx: number; matchIdx: number } | undefined,
  query: string
): string {
  const segments: { start: number; end: number; isCurrent: boolean }[] = [];

  for (let i = 0; i < pageMatches.length; i++) {
    const matchStart = pageMatches[i];
    const matchLen = pageMatchesLength?.[i] ?? 0;
    const matchEnd = matchStart + matchLen;

    // Check if this match overlaps with this text item
    if (matchEnd <= itemStart || matchStart >= itemEnd) continue;

    let overlapStart = Math.max(matchStart, itemStart) - itemStart;
    let overlapEnd = Math.min(matchEnd, itemEnd) - itemStart;
    const isCurrent = selected?.pageIdx === pageIndex && selected?.matchIdx === i;

    // When the full match is contained in this item, snap the highlight
    // to the actual query position within str. This corrects small offset
    // drift caused by normalization differences between PDFFindController
    // (disableNormalization:true) and react-pdf's text layer (NFKC-normalized).
    const isFullyContained = matchStart >= itemStart && matchEnd <= itemEnd;
    if (isFullyContained && query) {
      const idx = str.toLowerCase().indexOf(query.toLowerCase(), Math.max(0, overlapStart - 2));
      if (idx !== -1 && idx + query.length <= str.length) {
        overlapStart = idx;
        overlapEnd = idx + query.length;
      }
    }

    segments.push({ start: overlapStart, end: overlapEnd, isCurrent });
  }

  if (segments.length === 0) return str;

  // Sort segments by start offset
  segments.sort((a, b) => a.start - b.start);

  let result = "";
  let cursor = 0;

  for (const seg of segments) {
    if (seg.start > cursor) {
      result += escape(str.slice(cursor, seg.start));
    }
    const cls = seg.isCurrent ? "pdf-match pdf-match-current" : "pdf-match";
    result += `<mark class="${cls}">${escape(str.slice(seg.start, seg.end))}</mark>`;
    cursor = seg.end;
  }

  if (cursor < str.length) {
    result += escape(str.slice(cursor));
  }

  return result;
}
